/**
 * Permission grouping configuration for the role management UI.
 *
 * Permissions are grouped by module with optional parent-child hierarchy.
 * Cascade rules:
 * - Selecting an action auto-selects that module's `.access`
 * - Selecting a child's `.access` auto-selects the parent's `.access`
 * - Deselecting `.access` deselects all action permissions for that module
 * - Deselecting a parent's `.access` deselects all children's `.access` and actions
 */

export interface PermissionDef {
  codename: string
  label: string
}

export interface PermissionGroup {
  module: string
  label: string
  /** Module-level access permission. When present, cascade logic applies. */
  access: PermissionDef | null
  permissions: PermissionDef[]
  /** When set, this group is a child of the named parent module. */
  parentModule?: string
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    module: 'expenses',
    label: 'Expenses',
    access: { codename: 'expenses.access', label: 'Access' },
    permissions: [
      { codename: 'expenses.create', label: 'Create' },
      { codename: 'expenses.edit', label: 'Edit' },
      { codename: 'expenses.delete', label: 'Delete' },
    ],
  },
  {
    module: 'receipts',
    label: 'Receipts',
    parentModule: 'expenses',
    access: { codename: 'receipts.access', label: 'Access' },
    permissions: [
      { codename: 'receipts.create', label: 'Create' },
      { codename: 'receipts.edit', label: 'Edit' },
      { codename: 'receipts.delete', label: 'Delete' },
    ],
  },
  {
    module: 'reservations',
    label: 'Reservations',
    access: { codename: 'reservations.access', label: 'Access' },
    permissions: [
      { codename: 'reservations.create', label: 'Create' },
      { codename: 'reservations.edit', label: 'Edit' },
      { codename: 'reservations.delete', label: 'Delete' },
    ],
  },
  {
    module: 'vendors',
    label: 'Vendors',
    parentModule: 'expenses',
    access: { codename: 'vendors.access', label: 'Access' },
    permissions: [
      { codename: 'vendors.create', label: 'Create' },
      { codename: 'vendors.edit', label: 'Edit' },
      { codename: 'vendors.delete', label: 'Delete' },
    ],
  },
  {
    module: 'statements',
    label: 'Statements',
    parentModule: 'expenses',
    access: { codename: 'statements.access', label: 'Access' },
    permissions: [
      { codename: 'statements.create', label: 'Create' },
      { codename: 'statements.edit', label: 'Edit' },
      { codename: 'statements.delete', label: 'Delete' },
    ],
  },
  {
    module: 'export_templates',
    label: 'Export Templates',
    parentModule: 'expenses',
    access: { codename: 'export_templates.access', label: 'Access' },
    permissions: [
      { codename: 'export_templates.create', label: 'Create' },
      { codename: 'export_templates.edit', label: 'Edit' },
      { codename: 'export_templates.delete', label: 'Delete' },
    ],
  },
  {
    module: 'reports',
    label: 'Reports',
    parentModule: 'expenses',
    access: { codename: 'reports.access', label: 'Access' },
    permissions: [
      { codename: 'reports.create', label: 'Create' },
      { codename: 'reports.edit', label: 'Edit' },
      { codename: 'reports.delete', label: 'Delete' },
    ],
  },
  {
    module: 'chat',
    label: 'Chat',
    parentModule: 'reservations',
    access: { codename: 'chat.access', label: 'Access' },
    permissions: [],
  },
  {
    module: 'properties',
    label: 'Properties',
    access: { codename: 'properties.access', label: 'Access' },
    permissions: [],
  },
  {
    module: 'billing',
    label: 'Billing',
    access: { codename: 'billing.access', label: 'View balance' },
    permissions: [{ codename: 'billing.manage', label: 'Top up & usage' }],
  },
  {
    module: 'organization',
    label: 'Organization',
    access: null,
    permissions: [
      { codename: 'organization.manage_members', label: 'Manage Members' },
      { codename: 'organization.manage_roles', label: 'Manage Roles' },
    ],
  },
]

/** Get all child groups of a parent module. */
export function getChildGroups(groups: PermissionGroup[], parentModule: string): PermissionGroup[] {
  return groups.filter((g) => g.parentModule === parentModule)
}

/** Get the parent group of a child group, if any. */
export function getParentGroup(
  groups: PermissionGroup[],
  childModule: string,
): PermissionGroup | undefined {
  const child = groups.find((g) => g.module === childModule)
  if (!child?.parentModule) return undefined
  return groups.find((g) => g.module === child.parentModule)
}

/** Check if a group is a top-level group (no parent). */
export function isTopLevelGroup(group: PermissionGroup): boolean {
  return !group.parentModule
}
